import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import FormField from '@components/Common/FormField';
import type { ColumnsConfig } from '@components/Common/Table/cardFields';
import { useToast } from '@components/Common/Toast/ToastProvider';
import { isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import { markSubscriptionRefunded } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import ErrorMessage from '@components/Common/ErrorMessage';
import type { Column } from '@components/Common/SearchFilter/SearchFilter';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { Box, Button, Checkbox, Chip, FormControlLabel, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate } from '@helpers/date';
import { humanizeEnum } from '@helpers/labels';
import { canMarkRefunded, refundAmountError, subscriptionStatus } from './refund';
import './index.scss';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const SUBSCRIPTION_COLUMNS = ['employerName', 'planType', 'status', 'jobs', 'displayAmount', 'createdAt'];

/* The phone card: employer as the title, the status chip beside it, the action at the foot. */
const SUBSCRIPTION_CARD: ColumnsConfig = {
  employerName: { card: 'title', tablet: true },
  planType: { tablet: true },
  status: { card: 'status', tablet: true },
  displayAmount: { tablet: true },
  actions: { card: 'action', tablet: true },
};

interface Subscription {
  _id: string;
  employerId: string;
  employerName?: string;
  paymentId?: string;
  planType?: string;
  jobs: number;
  jobValidityDays?: number;
  currency?: string;
  amount?: string;
  createdAt?: string;
  createdBy?: string;
  /** ACTIVE | CANCELLED | REFUNDED; null on rows older than the field, which means ACTIVE. */
  status?: string | null;
  cancelledAt?: string | null;
  cancelledBy?: string | null;
  cancelReason?: string | null;
  refundedAt?: string | null;
  refundedBy?: string | null;
  refundAmount?: string | null;
  refundReference?: string | null;
  refundReason?: string | null;
}

const PLAN_CHIP_SX: Record<string, { backgroundColor: string; color: string }> = {
  PAY_PER_JOB: { backgroundColor: 'var(--color-background-muted)', color: 'var(--color-text-muted)' },
  LTD_TIER_1: { backgroundColor: 'var(--color-stage-qualified-bg)', color: 'var(--color-stage-qualified-text)' },
  LTD_TIER_2: { backgroundColor: 'var(--color-stage-proposal-bg)', color: 'var(--color-stage-proposal-text)' },
  LTD_TIER_3: { backgroundColor: 'var(--color-stage-won-bg)', color: 'var(--color-stage-won-text)' },
  // Managed engagements have no fixed job count and are priced per agreement. Without an entry
  // here they fell through to the PAY_PER_JOB chip and read as a 399 trial.
  MANAGED: { backgroundColor: 'var(--color-stage-proposal-bg)', color: 'var(--color-stage-proposal-text)' },
  // A free city-expansion post (promo code): a \u20b90 row that is neither a trial nor a purchase.
  PROMO_CITY: { backgroundColor: 'var(--color-stage-negotiation-bg)', color: 'var(--color-stage-negotiation-text)' },
};

const PLAN_LABEL_TO_VALUE: Record<string, string> = {
  'Pay Per Job': 'PAY_PER_JOB',
  'Ltd Tier 1': 'LTD_TIER_1',
  'Ltd Tier 2': 'LTD_TIER_2',
  'Ltd Tier 3': 'LTD_TIER_3',
  'Free City Post': 'PROMO_CITY',
};

// Sentence case, as the filter chips for the same values already read ("Ltd tier 3").
const formatPlanLabel = (planType?: string) => humanizeEnum(planType || 'PAY_PER_JOB');

const ROW_PER_PAGE = 50;
const FILTER_COLUMNS = ['Employer Name', 'Plan Type'];
/*
 * Display labels, deliberately. This page filters CLIENT-side in `filteredRows`, translating the
 * chosen label through PLAN_LABEL_TO_VALUE before comparing against the stored planType, so the
 * options here are the labels that map has keys for.
 *
 * Changing these to the stored enum values looks like the obvious fix -- planType really does
 * hold PAY_PER_JOB -- and breaks the page: the lookup misses, planValue becomes '', and the
 * filter silently stops filtering. It has to stay in step with PLAN_LABEL_TO_VALUE.
 */
const COLUMN_OPTIONS: Record<string, string[]> = {
  'Plan Type': ['Pay Per Job', 'Ltd Tier 1', 'Ltd Tier 2', 'Ltd Tier 3', 'Free City Post'],
};

const Subscriptions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterData, setFilterData] = useState<Column[]>([]);
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    return Number.isFinite(p) && p >= 0 ? p : 0;
  });
  const skipResetOnFirstFilterChange = useRef(true);
  const { showToast } = useToast();
  const canRefund = isSuperAdmin(useCurrentUserRole());
  const columns = useMemo(
    () => (canRefund ? [...SUBSCRIPTION_COLUMNS, 'actions'] : SUBSCRIPTION_COLUMNS),
    [canRefund],
  );
  // The purchase whose refund is being recorded, and the dialog's own fields.
  const [refunding, setRefunding] = useState<Subscription | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReference, setRefundReference] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundCloseJobs, setRefundCloseJobs] = useState(false);
  const [refundSaving, setRefundSaving] = useState(false);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/subscriptions');
      setData(response.data?.content || []);
    } catch (err: any) {
      // The bare `catch {}` here threw away the server's own `{ error }` body and its status, so
      // a 500 and a 403 produced the same sentence and the console said nothing either.
      const status = err?.response?.status;
      const detail = err?.response?.data?.error;
      console.error('[Subscriptions] fetch failed', status, detail ?? err?.message);
      setError(
        detail
          ? `${detail}${status ? ` (${status})` : ''}`
          : 'Failed to fetch subscriptions. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const openRefund = (sub: Subscription) => {
    // Prefilled with what was paid: a full refund is the common case, and a partial one is an
    // edit of that number rather than a sum someone works out from the gateway again.
    setRefundAmount(sub.amount ?? '');
    setRefundReference('');
    setRefundReason('');
    setRefundCloseJobs(false);
    setRefunding(sub);
  };

  const amountError = refunding ? refundAmountError(refundAmount, refunding.amount) : null;
  const refundInvalid = !!amountError || !refundReference.trim();

  const recordRefund = async () => {
    if (!refunding || refundInvalid) return;
    setRefundSaving(true);
    try {
      await fetchGraphQLApiData(
        markSubscriptionRefunded(
          refunding._id,
          refundAmount.trim(),
          refundReference.trim(),
          refundReason.trim(),
          refundCloseJobs,
        ),
      );
      setRefunding(null);
      showToast('Refund recorded and plan cancelled', 'success');
      await fetchSubscriptions();
    } catch (e: any) {
      const detail = String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '');
      showToast(detail || 'Could not record the refund. Nothing was changed.', 'error');
    } finally {
      setRefundSaving(false);
    }
  };

  const onApplyFilters = useCallback(() => {
    if (skipResetOnFirstFilterChange.current) {
      skipResetOnFirstFilterChange.current = false;
      return;
    }
    setPage(0);
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('page'); return n; });
  }, []);

  const filteredRows = useMemo(() => {
    const employerSearch = filterData.find(f => f.columnName === 'Employer Name')?.searchValue?.toLowerCase() || '';
    const planLabel = filterData.find(f => f.columnName === 'Plan Type')?.searchValue || '';
    const planValue = PLAN_LABEL_TO_VALUE[planLabel] || '';

    return data
      .filter(s => {
        if (employerSearch && !(s.employerName || '').toLowerCase().includes(employerSearch)) return false;
        if (planValue && s.planType !== planValue) return false;
        return true;
      })
      .map(s => ({
        id: s._id,
        employerId: s.employerId,
        employerName: s.employerName || s.employerId,
        planType: s.planType,
        jobs: s.jobs,
        displayAmount: s.planType === 'PROMO_CITY' ? 'Free' : s.amount ? `${s.currency ?? ''} ${s.amount}`.trim() : '-',
        createdAt: s.createdAt,
        status: subscriptionStatus(s.status),
        refundAmount: s.refundAmount,
        refundReference: s.refundReference,
        currency: s.currency,
        source: s,
      }));
  }, [data, filterData]);

  const pageRows = useMemo(
    () => filteredRows.slice(page * ROW_PER_PAGE, (page + 1) * ROW_PER_PAGE),
    [filteredRows, page],
  );

  return (
    <>
    <CRMPageTemplate
      columnOptions={COLUMN_OPTIONS}
      filterColumns={FILTER_COLUMNS}
      filterLabel="Filter"
      icon={<CreditCardIcon />}
      setFilterData={setFilterData}
      title="Subscriptions"
      totalCount={filteredRows.length}
      onApplyFilters={onApplyFilters}
    >
      {loading ? (
        <SkeletonLoader rows={20} columns={columns.length} />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : (
        <Box className="subs-table">
        <MintlyTable
          pagination={{
            enabled: true,
            page,
            rowsPerPage: ROW_PER_PAGE,
            totalNumberOfRows: filteredRows.length,
            handleChangePage: (_: unknown, newPage: number) => { setPage(newPage); setSearchParams(prev => { const n = new URLSearchParams(prev); if (newPage > 0) n.set('page', String(newPage)); else n.delete('page'); return n; }); },
          }}
          rows={pageRows}
          selectors={columns}
          columns={SUBSCRIPTION_CARD}
          tableHeadRow={{
            employerName: <>Employer</>,
            planType: <>Plan</>,
            status: <>Status</>,
            actions: <></>,
            jobs: <>Jobs</>,
            displayAmount: <>Amount</>,
            createdAt: <>Date</>,
          }}
          tableBodyRow={{
            employerName: (row: any) => (
              <Typography
                component="span"
                sx={{
                  color: 'var(--color-accent)',
                  cursor: 'pointer',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' },
                  '&:focus-visible': { outline: '2px solid var(--color-accent)', borderRadius: '2px' },
                }}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/employers/${row.employerId}`); }}
                onClick={(e) => { e.stopPropagation(); navigate(`/employers/${row.employerId}`); }}
              >
                {row.employerName}
              </Typography>
            ),
            planType: (row: any) => {
              const sx = PLAN_CHIP_SX[row.planType ?? ''] ?? PLAN_CHIP_SX.PAY_PER_JOB;
              return (
                <Chip
                  label={formatPlanLabel(row.planType)}
                  size="small"
                  sx={sx}
                />
              );
            },
            status: (row: any) => (
              <CRMStatusChip status={row.status} label={humanizeEnum(row.status)} />
            ),
            actions: (row: any) =>
              canMarkRefunded(row.status, row.source?.amount) ? (
                <Box
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={refundSaving}
                    onClick={() => openRefund(row.source)}
                  >
                    Mark refunded
                  </Button>
                </Box>
              ) : row.refundAmount ? (
                <Typography variant="body2" component="span" sx={{ color: 'var(--tx-mid)' }}>
                  {`Refunded ${`${row.currency ?? ''} ${row.refundAmount}`.trim()}`}
                </Typography>
              ) : null,
            jobs: (row: any) => <>{row.jobs ?? '-'}</>,
            displayAmount: (row: any) => <>{row.displayAmount}</>,
            createdAt: (row: any) => (
              <>{formatDate(row.createdAt)}</>
            ),
          }}
        />
        </Box>
      )}
    </CRMPageTemplate>

    {/* Records a refund; it does not make one. The money moves in the gateway's own dashboard,
        and this writes down that it did, then cancels the plan the purchase paid for. */}
    <ConfirmDialog
      open={!!refunding}
      title="Mark this purchase refunded?"
      body={
        refunding
          ? `This only records a refund you have already made in the Razorpay or Stripe dashboard; no money moves from here. It also cancels ${refunding.employerName || 'this employer'}'s plan and removes their remaining job credits.`
          : ''
      }
      actionLabel="Mark refunded"
      busy={refundSaving}
      confirmDisabled={refundInvalid}
      onCancel={() => setRefunding(null)}
      onConfirm={() => void recordRefund()}
    >
      <FormField
        required
        name="refundAmount"
        label={`Amount refunded${refunding?.currency ? ` (${refunding.currency})` : ''}`}
        value={refundAmount}
        onChange={(_n, v) => setRefundAmount(String(v))}
        error={!!amountError}
        helperText={amountError ?? undefined}
        textFieldProps={{ slotProps: { htmlInput: { inputMode: 'decimal' } } }}
      />
      <FormField
        required
        name="refundReference"
        label="Refund reference"
        placeholder="The refund id from the gateway, e.g. rfnd_..."
        value={refundReference}
        onChange={(_n, v) => setRefundReference(String(v))}
      />
      <FormField
        required={false}
        name="refundReason"
        label="Reason (optional)"
        value={refundReason}
        onChange={(_n, v) => setRefundReason(String(v))}
        multiline
        rows={2}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={refundCloseJobs}
            onChange={(e) => setRefundCloseJobs(e.target.checked)}
          />
        }
        label="Also close their live jobs"
      />
    </ConfirmDialog>
    </>
  );
};

export default Subscriptions;
